import { useQuery } from '@tanstack/react-query';

import type { OrderDocuments } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get order documents by ID
 * @param servicesProviderId - Required services provider ID
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const getOrderDocuments = async (
	servicesProviderId: string,
	orderId: string,
): Promise<OrderDocuments> => {
	const response = await apiClient.get<OrderDocuments>(
		ordersEndpoints.documents(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for fetching order documents by ID
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetOrderDocuments = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.documents.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to fetch order documents without an order ID.');
			}
			return getOrderDocuments(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
